import { FeatureFlagKey } from '../hooks/useFeatureFlags';
import {
  AppCategory,
  MenuSectionTitle,
  type EntityApplication,
  type GabDynamicEntitlementKey,
  type RemoteAppConfig,
  type ExternalUrlAppConfig,
  type UnresolvedAppConfig,
} from '../types';
import { GAB_APPS_MOUNT_PATH, gabCoreOrigin, gabCoreModuleUrl } from './gabCoreModule';

/** Trusted legacy v1 origin from this build's required environment configuration. */
export const gabV1Origin = (): string => import.meta.env.VITE_GAB_V1_ORIGIN;

/**
 * GAB registers one Access Control entitlement per app, keyed `j` (v2) or `m` (v1) plus the app's
 * 9-character base36 key (Access Control requires a leading letter, and an app key can start
 * with a digit). The key is created at runtime, so no static registry entry can name it.
 *
 * The key shape is the only marker of a gab-core app entitlement; recognizing it by its claim
 * instead is tracked in GABSW-5563.
 */
const GAB_DYNAMIC_ENTITLEMENT_KEY = /^[jm][a-z0-9]{9}$/;

export const isGabDynamicEntitlementKey = (key: string): key is GabDynamicEntitlementKey =>
  GAB_DYNAMIC_ENTITLEMENT_KEY.test(key);

/** Dynamic GAB entries use either the shared v2 module or a legacy v1 iframe. */
export type GabDynamicAppConfig = (
  | (RemoteAppConfig & {
      moduleRoutes: NonNullable<RemoteAppConfig['moduleRoutes']>;
      externalUrl?: never;
    })
  | (ExternalUrlAppConfig & { openInNewTab: false })
) & {
  id: GabDynamicEntitlementKey;
  category: AppCategory.ENTITLED;
};

/**
 * Where the entries sit in the sidebar: in Products, each app its own item among the products,
 * treated like any product. While `psp-ui-all-products-page` is on, the sidebar shows at most 7
 * products (gab-core apps included), the rest are on the All Products page, and a pinned app is
 * listed first. While it is off there is no cap, so the sidebar lists every app.
 */
const GAB_APP_MENU_SECTION = MenuSectionTitle.PRODUCTS;

/** An app's address in the shell: the mount path plus the route its entitlement URL names. */
const gabCoreAppBasePath = (startPath: string): string => `${GAB_APPS_MOUNT_PATH}${startPath}`;

/** Trimmed string, or `undefined` when the API sent nothing usable. */
const nonEmptyText = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/** `path` without trailing slashes. A scan rather than a regex, which backtracks on long runs. */
const stripTrailingSlashes = (path: string): string => {
  let end = path.length;
  while (end > 0 && path[end - 1] === '/') end--;
  return path.slice(0, end);
};

/**
 * The route inside gab-core that an entitlement URL names, or `null` when the URL cannot be
 * trusted or names no route. The URL is data an app admin can edit, so it must be an https URL on
 * gab-core's own origin and carry no credentials; only its path is used, so a query string or
 * fragment never reaches the module. A bare origin names no app, so it yields `null`.
 */
export function gabCoreAppStartPath(url: unknown): string | null {
  return trustedAppPath(url, gabCoreOrigin());
}

function trustedAppPath(url: unknown, origin: string): string | null {
  if (typeof url !== 'string') return null;
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== 'https:' || parsed.origin !== origin) return null;
  if (parsed.username !== '' || parsed.password !== '') return null;
  const path = stripTrailingSlashes(parsed.pathname);
  return path === '' ? null : path;
}

function toGabDynamicAppConfig(
  key: GabDynamicEntitlementKey,
  entitlement: EntityApplication,
  basePath: string,
  iframe: boolean
): GabDynamicAppConfig {
  const appKey = key.slice(1);
  const description = nonEmptyText(entitlement.description);
  const common = {
    id: key,
    category: AppCategory.ENTITLED as const,
    basePath,
    displayName: nonEmptyText(entitlement.displayName) ?? appKey,
    ...(description ? { description } : {}),
    icon: 'ViewSplitVertical' as const,
    menuSection: GAB_APP_MENU_SECTION,
    featureFlagKey: FeatureFlagKey.PSP_UI_DYNAMIC_GAB_APPS,
  };
  if (iframe) {
    return {
      ...common,
      externalUrl: () => entitlement.url,
      openInNewTab: false,
      triggerPageRefreshOnNavigateAway: (ctx) =>
        !!ctx.featureFlags?.[FeatureFlagKey.PSP_UI_LSF_PAGE_REFRESH],
    };
  }
  return {
    ...common,
    // A sidebar item only: the shared `/a` mount in the route registry serves its path.
    navigationOnly: true,
    // The shared `/a` mount (`RouteRegistryId.GAB_APPS`) owns the page refresh; a refresh path
    // per app would reload the page between two gab-core apps, which share one module instance.
    triggerPageRefreshOnNavigateAway: () => false,
    moduleRoutes: [{ pattern: '/', resolver: gabCoreModuleUrl }],
  };
}

/** Why a gab-core app entitlement yielded no registry entry. */
export type GabAppSkipReason =
  /** The URL is missing, not https, not on a trusted GAB origin, carries credentials, or names no route. */
  | 'url'
  /** A static registry entry already has this id; the static product wins. */
  | 'static-entry'
  /** An earlier entitlement in the list has the same key. */
  | 'duplicate-key'
  /** An earlier app's URL names the same route, so this one would share its address. */
  | 'duplicate-route';

export interface SkippedGabAppEntitlement {
  entitlementKey: GabDynamicEntitlementKey;
  reason: GabAppSkipReason;
}

interface SeenGabApps {
  readonly staticIds: ReadonlySet<string>;
  readonly keys: ReadonlySet<string>;
  readonly paths: ReadonlySet<string>;
}

function skipReason(
  key: GabDynamicEntitlementKey,
  basePath: string | null,
  seen: SeenGabApps
): GabAppSkipReason | null {
  if (seen.staticIds.has(key)) return 'static-entry';
  if (seen.keys.has(key)) return 'duplicate-key';
  if (basePath === null) return 'url';
  if (seen.paths.has(basePath)) return 'duplicate-route';
  return null;
}

export interface GabDynamicApps {
  readonly configs: readonly GabDynamicAppConfig[];
  /** Every gab-core app entitlement that yielded no entry, so a missing item can be traced. */
  readonly skipped: readonly SkippedGabAppEntitlement[];
}

/**
 * One registry entry per gab-core app entitlement among `entitlements`, so each app gets its
 * own sidebar item, using the shared v2 module or a per-app legacy v1 iframe.
 *
 * An entitlement yields no entry when its key is not a gab-core app key, when a static entry
 * already names the key (the static product wins), or when its URL is missing or not on a trusted GAB
 * origin: without a route to open there is nothing to show. A repeated key, or a second app whose
 * URL names the same route, yields one entry. Every gab-core app entitlement that yields no entry
 * is returned in `skipped` with the reason. The function is pure; the caller decides whether to
 * log the skips.
 */
export function buildGabDynamicAppConfigs(
  staticRegistry: readonly UnresolvedAppConfig[],
  entitlements: readonly EntityApplication[]
): GabDynamicApps {
  const staticIds = new Set<string>(staticRegistry.map((app) => app.id));
  const keys = new Set<string>();
  const paths = new Set<string>();
  const configs: GabDynamicAppConfig[] = [];
  const skipped: SkippedGabAppEntitlement[] = [];
  for (const entitlement of entitlements) {
    const key = entitlement.entitlementProductKey;
    if (!isGabDynamicEntitlementKey(key)) continue;
    const startPath = gabCoreAppStartPath(entitlement.url);
    const iframePath = trustedAppPath(entitlement.url, gabV1Origin());
    const iframe = startPath === null && iframePath !== null;
    const basePath =
      startPath !== null
        ? gabCoreAppBasePath(startPath)
        : iframe
          ? `/b/apps/${key.slice(1)}`
          : null;
    const reason = skipReason(key, basePath, { staticIds, keys, paths });
    if (reason !== null || basePath === null) {
      skipped.push({ entitlementKey: key, reason: reason ?? 'url' });
      continue;
    }
    keys.add(key);
    paths.add(basePath);
    configs.push(toGabDynamicAppConfig(key, entitlement, basePath, iframe));
  }
  return { configs, skipped };
}
